import { RadioLayout } from "@tree-company/tree-query";
import { PageComponent } from "./components/page_component";
import { apiClient } from "./client";
import { translatedPropertyFromKey } from "./locales";
import { NextStatements } from "./components/next_statements";
import { STATEMENT_KEYS, STATEMENTS } from "./data/statements";

let aggregatorURL = import.meta.env.VITE_AGGREGATOR_URL;
if (aggregatorURL === undefined && import.meta.env.DEV) {
  aggregatorURL = "http://localhost:3300";
}

function getPreviousAnswer(key) {
  // We select the second to last one, since the last treesponse is the current one
  return apiClient.treesponses.at(-2)?.getAnswer(key);
}
const OPTION_KEYS = ["1", "2", "3", "4", "5"];
const aiResponseTexts = {
  proficiencyOkToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_ok",
  ),
  proficiencyOkToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_ok_toxicity_not_ok",
  ),
  proficiencyHighToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_ok",
  ),
  proficiencyHighToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_high_toxicity_not_ok",
  ),
  proficiencyLowToxicityOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_ok",
  ),
  proficiencyLowToxicityNotOk: translatedPropertyFromKey(
    "ai_feedback.proficiency_low_toxicity_not_ok",
  ),
};

export function surveySettingsForStatement(statement) {
  const answeredStatements = apiClient.treesponses.map((s) =>
    s.getAnswer("statement"),
  );
  const availableStatements = STATEMENT_KEYS.filter(
    (s) => !answeredStatements.includes(s) && statement !== s,
  ).sort((s1, s2) => STATEMENTS[s1].order - STATEMENTS[s2].order);

  const statementName = translatedPropertyFromKey([
    "statements",
    statement,
    "name",
  ]);
  return {
    key: `niras-${statement}`,
    pages: [
      {
        key: "opinion",
        order: 1,
        component: PageComponent,
        showNext: false,
        statement,
        htmlAttributes: {
          class: "survey--compact",
        },
        sections: [
          {
            type: "radio",
            htmlAttributes: {
              class: "question--statement",
            },
            order: 1,
            key: "opinion",
            layout: RadioLayout.buttons,
            title: statementName,
            description: translatedPropertyFromKey([
              "statementQuestion",
              "description",
            ]),
            options: OPTION_KEYS.map((option) => ({
              label: translatedPropertyFromKey([
                "statementQuestion",
                "options",
                option,
                "text",
              ]),
              description: translatedPropertyFromKey([
                "statementQuestion",
                "options",
                option,
                "text",
              ]),
              value: option,
            })),
          },
        ],
      },
      {
        key: `comment-${statement}`,
        order: 3,
        component: PageComponent,
        statement,
        sections: [
          {
            order: 1,
            type: "text",
            key: "comment",
            maxLength: 1000,
            title: translatedPropertyFromKey("comment.title"),
            description: translatedPropertyFromKey("comment.description"),
            placeholder: translatedPropertyFromKey("comment.placeholder"),
            aiResponseTexts,
            required: true,
          },
          {
            order: 98,
            type: "hidden",
            key: "opinion_bucket",
            dependsOn: ["opinion"],
            computeValue: (treesponse) => {
              const value = treesponse.getAnswer("opinion");
              if (["1", "2"].includes(value)) return "1_2";
              if (["4", "5"].includes(value)) return "4_5";
              return value;
            },
          },
        ],
      },
      {
        key: "name",
        order: 4,
        component: PageComponent,
        title: translatedPropertyFromKey(["name", "intro", "title"]),
        description: translatedPropertyFromKey([
          "name",
          "intro",
          "description",
        ]),
        statement,
        sections: [
          {
            order: 1,
            type: "string",
            key: "firstName",
            title: translatedPropertyFromKey(["name", "firstName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("firstName"),
          },
          {
            order: 2,
            type: "string",
            key: "lastName",
            title: translatedPropertyFromKey(["name", "lastName", "title"]),
            required: true,
            defaultValue: () => getPreviousAnswer("lastName"),
          },
          {
            order: 3,
            type: "hidden",
            key: "completed",
            defaultValue: true,
          },
        ],
      },
      {
        key: "sociodemo",
        order: 5,
        component: PageComponent,
        title: translatedPropertyFromKey(["sociodemo", "intro", "title"]),
        description: translatedPropertyFromKey([
          "sociodemo",
          "intro",
          "description",
        ]),
        // We skip this page if the user answered the same name as their previous answer
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            order: 1,
            type: "number",
            key: "yearOfBirth",
            title: translatedPropertyFromKey([
              "sociodemo",
              "yearOfBirth",
              "title",
            ]),
            options: [],
            defaultValue: () => getPreviousAnswer("yearOfBirth"),
            maxLength: 4,
            required: true,
            placeholder: translatedPropertyFromKey([
              "sociodemo",
              "yearOfBirth",
              "placeholder",
            ]),
          },
          {
            order: 1,
            type: "string",
            key: "postcode",
            title: translatedPropertyFromKey([
              "sociodemo",
              "postcode",
              "title",
            ]),
            options: [],
            defaultValue: () => getPreviousAnswer("postcode"),
            maxLength: 6,
            required: true,
            placeholder: translatedPropertyFromKey([
              "sociodemo",
              "postcode",
              "placeholder",
            ]),
          },
        ],
      },
      {
        key: "like",
        order: 6,
        component: PageComponent,
        statement,
        sections: [
          {
            type: "like",
            order: 1,
            key: "like",
            title: translatedPropertyFromKey(["like", "title"]),
            description: translatedPropertyFromKey(["like", "description"]),
            aggregatorSettings: {
              url: `${aggregatorURL}/niras`,
              projectKey: import.meta.env.VITE_AGGREGATOR_KEY,
            },
          },
        ],
      },
      {
        key: "optin",
        order: 7,
        component: PageComponent,
        title: translatedPropertyFromKey(["optin", "intro", "title"]),
        // We only show the opt in page if the user has not encountered it before
        skipIf: (treesponse) => {
          return ["firstName", "lastName"].every(
            (key) => treesponse.getAnswer(key) === getPreviousAnswer(key),
          );
        },
        sections: [
          {
            type: "checkbox",
            order: 1,
            title: translatedPropertyFromKey([
              "optin",
              "optinProject",
              "title",
            ]),
            key: "optinProject",
          },
          {
            type: "checkbox",
            order: 2,
            title: translatedPropertyFromKey([
              "optin",
              "optinOrganisation",
              "title",
            ]),
            key: "optinOrganisation",
          },
          {
            type: "email",
            order: 3,
            skipIf: (treesponse) =>
              treesponse.getAnswer("optinProject") !== true &&
              treesponse.getAnswer("optinOrganisation") !== true,
            dependsOn: ["optinProject", "optinOrganisation"],
            title: translatedPropertyFromKey(["optin", "email", "title"]),
            key: "email",
            customValidations: [
              (treesponse, _key, value) => {
                if (
                  (treesponse.getAnswer("optinProject") ||
                    treesponse.getAnswer("optinOrganisation")) &&
                  (value === null || value === undefined || value.trim() === "")
                ) {
                  return [
                    false,
                    translatedPropertyFromKey(["optin", "email", "required"]),
                  ];
                }

                return [true, null];
              },
            ],
          },
        ],
      },
      {
        key: "next",
        order: 8,
        component: PageComponent,
        title: translatedPropertyFromKey(["next", "intro", "title"]),
        description: translatedPropertyFromKey([
          "next",
          "intro",
          availableStatements.length > 0
            ? "description"
            : "description_all_done",
        ]),
        sections: [
          {
            type: "content",
            component: NextStatements,
            order: 1,
            availableStatements,
          },
        ],
        showNext: false,
        htmlAttributes: {
          class: "survey--compact",
        },
      },
    ],
  };
}
